import React, { FC, useEffect, useState } from 'react';

import { Select } from 'chakra-react-select';
import { FormControl, FormLabel, Text } from '@chakra-ui/react';

import { useField, useFormikContext } from 'formik';

import { SiteFilterableSelectProps } from './SiteFilterableSelect.props';
import getSortedData from 'utils/getSortedData';
import { fetchSites } from 'api/site';
import { useQuery, useQueryClient } from 'react-query';
import { washHub } from 'store';
import { Site } from 'types/site';

type OptionType = { label: string; value: string };

const SiteFilterableSelectView: FC<SiteFilterableSelectProps> = ({
  values,
  isReadOnly,
  // isLoading,
  containerProps = {},
  label,
  name,
  labelProps,
  isRequired = true,
  selectedInventoryItem,
  onChange,
  elementContainerProps = {},
}) => {
  const queryClient = useQueryClient();
  const [{ value }, meta, field] = useField<string[]>(name);
  const [options, setOptions] = useState<OptionType[]>([]);
  const isWashHubIdValid = washHub.selectors.useIsWashHubIdValid();
  const { setFieldValue } = useFormikContext<any>();
  const washHubId = washHub.selectors.useSelectedWashHubId();
  const queryKey = ['fetch-sites-active', washHubId];
  const queryFn = () => fetchSites(washHubId, 1);

  const { data = [], isLoading } = useQuery(queryKey, queryFn, {
    enabled: isWashHubIdValid, // Ensure query is only enabled when washHubId is truthy
    onSuccess: (data) => {
      const _opt = getSortedData(data).map((p) => ({
        label: p.name,
        value: p._id,
      }));
      setOptions(_opt);
    },
  });
  const _values = value
    ? data
        ?.filter((p) => value?.includes(p._id) || value?.includes(p.name))
        ?.map((p) => ({ label: p.name, value: p._id }))
    : undefined;
  useEffect(() => {
    // Access cached data
    const cachedData = queryClient.getQueryData<Site[]>(queryKey);
    if (cachedData && options.length === 0) {
      const _opt = getSortedData(cachedData).map((p) => ({
        label: p.name,
        value: p._id,
      }));
      setOptions(_opt);
    }
  }, [queryClient, queryKey, options.length]);

  useEffect(() => {
    if (selectedInventoryItem) {
      const selectedInventoryItemId: string = selectedInventoryItem?.site;
      setFieldValue(name, selectedInventoryItemId);
    }
  }, []);

  return (
    <FormControl {...containerProps} mt={3} isRequired={isRequired}>
      <FormLabel
        htmlFor={name}
        textTransform="capitalize"
        fontSize="14px"
        color="#afafaf"
        {...labelProps}>
        {label}
      </FormLabel>
      <Select
        name={name}
        placeholder="Select"
        value={_values}
        isDisabled={isLoading || isReadOnly}
        isLoading={isLoading}
        options={options}
        tagVariant="solid"
        colorScheme="primary"
        closeMenuOnSelect={true}
        chakraStyles={{
          container: (_props) => ({
            ..._props,
            ...elementContainerProps,
            backgroundColor: isReadOnly ? '#EBEBEB' : 'FFFFFF',
          }),
          dropdownIndicator: (_props) => ({
            ..._props,
            bg: 'transparent',
          }),
          option: (_props) => ({
            ..._props,
            padding: '10px',
            borderBottom: '1px solid #ccc',
          }),
          menuList: (_props) => ({
            ..._props,
            padding: '0px',
            overflow: 'hidden',
          }),
          menu: (_props) => ({
            ..._props,
            zIndex: 20,
          }),
        }}
        onChange={(newValue) => {
          const site = newValue?.value;
          const siteCode = data?.find((s) => s._id === site)?.code;

          setFieldValue(name, site);
          setFieldValue('siteCode', siteCode);

          site && onChange?.(site);
        }}
      />
      {meta.error && meta.touched && (
        <Text color="red.500" fontSize="13px">
          {meta.error}
        </Text>
      )}
    </FormControl>
  );
};

export default SiteFilterableSelectView;
